import React from 'react';
import { createPortal } from 'react-dom';
import { FmOverlayPortalContext } from './FmOverlayPortalContext';
import { FmThemeIsland } from '@/shared/ui/FmThemeProvider';

export type FmDrawerPlacement = 'right' | 'bottom';

export interface FmDrawerProps {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children?: React.ReactNode;
  /** Optional footer region (actions). Pass nothing to omit. */
  footer?: React.ReactNode;
  /**
   * Anchor edge. `right` slides a side sheet in from the trailing edge (desktop);
   * `bottom` renders a bottom sheet (tablet/mobile). Consumers pick per device
   * tier — the primitive stays presentational.
   */
  placement?: FmDrawerPlacement;
  /**
   * Declares an explicit theme island on the drawer panel via `data-theme`. Use
   * `light` so the solid data body stays crisp even when the host route is dark
   * ("chrome is glass, content is solid"). When omitted, the drawer inherits the
   * ambient route theme.
   */
  theme?: 'light' | 'dark';
  /** Semantic panel width for `right` placement. Token-backed via `--fm-drawer-w-*`. */
  size?: 'sm' | 'md' | 'lg';
  /** Accessible label when no visible `title` is provided. */
  ariaLabel?: string;
  /** Localized accessible label for the close button (defaults to "Close"). */
  closeLabel?: string;
  'data-testid'?: string;
}

const PANEL_WIDTH_BY_SIZE: Record<'sm' | 'md' | 'lg', string> = {
  sm: 'var(--fm-drawer-w-sm)',
  md: 'var(--fm-drawer-w-md)',
  lg: 'var(--fm-drawer-w-lg)',
};

const cx = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join(' ');

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function getFocusableElements(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => !el.hasAttribute('disabled') && el.getAttribute('aria-hidden') !== 'true',
  );
}

/**
 * FmDrawer — Lehti AI DS side/bottom sheet. The reusable drawer chrome: portal to
 * <body>, frosted backdrop, Escape-to-close, body scroll lock while open, focus
 * trap + focus restore. Replaces AntD `Drawer`. The panel body may declare its
 * own light theme island (`theme="light"`) so data cards stay solid over a dark
 * host. Source preview: shell-anatomy.html (right-drawer / bottom-sheet anatomy).
 */
export const FmDrawer: React.FC<FmDrawerProps> = ({
  open,
  onClose,
  title,
  children,
  footer,
  placement = 'right',
  theme,
  size = 'md',
  ariaLabel,
  closeLabel = 'Close',
  'data-testid': dataTestId,
}) => {
  const titleId = React.useId();
  const panelRef = React.useRef<HTMLDialogElement | null>(null);
  const previouslyFocusedRef = React.useRef<HTMLElement | null>(null);
  const onCloseRef = React.useRef(onClose);

  React.useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const handleKeyDown = React.useCallback((e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      onCloseRef.current?.();
      return;
    }
    if (e.key !== 'Tab') return;
    const panel = panelRef.current;
    if (!panel) return;
    const focusable = getFocusableElements(panel);
    if (focusable.length === 0) {
      e.preventDefault();
      panel.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  React.useEffect(() => {
    if (!open) return;
    previouslyFocusedRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const onKey = (e: KeyboardEvent) => handleKeyDown(e);
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focusFrame = window.requestAnimationFrame(() => {
      const panel = panelRef.current;
      if (!panel) return;
      const [firstFocusable] = getFocusableElements(panel);
      (firstFocusable ?? panel).focus();
    });

    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      previouslyFocusedRef.current?.focus?.();
      previouslyFocusedRef.current = null;
    };
  }, [open, handleKeyDown]);

  if (!open) return null;

  const panelStyle = placement === 'right' ? { width: PANEL_WIDTH_BY_SIZE[size] } : undefined;

  return createPortal(
    <FmOverlayPortalContext.Provider value={panelRef}>
      <div className={cx('fm-drawer__root', `fm-drawer--${placement}`)} data-testid={dataTestId}>
        <div className="fm-drawer__backdrop" onClick={onClose} aria-hidden="true" />
        <FmThemeIsland theme={theme}>
        <dialog
          open
          ref={panelRef}
          className="fm-drawer__panel"
          aria-modal="true"
          aria-labelledby={title ? titleId : undefined}
          aria-label={title ? undefined : ariaLabel}
          tabIndex={-1}
          style={panelStyle}
        >
          <div className="fm-drawer__header">
            {title ? (
              <h2 className="fm-drawer__title" id={titleId}>
                {title}
              </h2>
            ) : (
              <span />
            )}
            <button
              type="button"
              className="fm-drawer__close"
              onClick={onClose}
              aria-label={closeLabel}
              data-testid={dataTestId ? `${dataTestId}-close` : undefined}
            >
              ✕
            </button>
          </div>
          <div className="fm-drawer__body">{children}</div>
          {footer ? <div className="fm-drawer__footer">{footer}</div> : null}
        </dialog>
        </FmThemeIsland>
      </div>
    </FmOverlayPortalContext.Provider>,
    document.body,
  );
};

export default FmDrawer;
